import { useMondayItemManager } from "../components/monday-item-manager-context";
import { useEffect, useState } from "react";
import { MondayErrorCodes, MondayItemData } from "shared/integrations/monday-types";

type ErrorWithCode = Error & { code: MondayErrorCodes };

type ItemOrError = { data: MondayItemData; error?: never } | { data?: never; error: ErrorWithCode };

/**
 * Hook to fetch and subscribe to multiple Monday items
 * @param itemIds - Array of Monday item IDs
 */
export function useMondayItemsInBulk(itemIds: string[]): {
  data: Record<string, ItemOrError> | null;
  isLoading: boolean;
  error: Error | undefined;
} {
  const manager = useMondayItemManager();
  const [value, setValue] = useState<null | Record<string, ItemOrError>>(null);

  useEffect(() => {
    let currentState: typeof value = null;
    for (const itemId of itemIds) {
      const item = manager.getSingleItem(itemId);
      if (item?.data) {
        currentState ??= {};
        currentState[itemId] = { data: item.data };
      } else if (item?.error) {
        currentState ??= {};
        const error = new Error(item.error.message) as ErrorWithCode;
        error.code = item.error.code;
        currentState[itemId] = { error };
      }
    }
    setValue(currentState);

    // Subscribe to each item
    // todo: we need a function to subscribe in bulk
    const unsubscribe: Array<(() => void) | undefined> = [];
    for (const itemId of itemIds) {
      unsubscribe.push(
        manager.subscribe(itemId, {
          next: (data) => {
            if (data) {
              setValue((previous: Record<string, ItemOrError> | null) => {
                const oldValues = previous || {};
                return {
                  ...oldValues,
                  [itemId]: { data },
                };
              });
            }
          },
          error: (error: Error) => {
            setValue((previous: Record<string, ItemOrError> | null) => {
              const oldValues = previous || {};
              return {
                ...oldValues,
                [itemId]: { error: error as ErrorWithCode },
              };
            });
          },
          complete: () => {},
        })
      );
    }

    return () => {
      for (const callback of unsubscribe) {
        callback?.();
      }
    };
  }, [itemIds, manager]);

  return {
    data: value,
    isLoading: !value,
    error: undefined,
  };
}
